import React, {ChangeEvent, FC, useEffect, useRef, useState} from 'react';
import Api from "../../../../api";
import FullscreenModal from "components/FullscreenModal";
import styles from "./components.module.scss";
import MaterialInput from "components/Inputs/MaterialInput";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import clsx from "clsx";
import {SimpleModal} from "components/Modal";
import {RemoveContactInfoModal} from "./RemoveContactinfoModal";
import {ChooseBio} from "./ChooseBio";
import FixedLoader from "components/FixedLoader/FixedLoader";


interface AboutYouProps {
    about?: string,
    name?: string,
    isOpen: boolean,
    onClose: () => void,
    onSaveAbout: (about: string) => void
}

const countWords = (inputString: string): number => {
    const words: string[] = inputString.trim().split(/\s+/);
    return words.length;
}

const tryLaterText = "Sorry, you've used all of your profile assists for today. Please skip or try again tomorrow."

export const AboutYou: FC<AboutYouProps> = ({
    about,
    name,
    isOpen,
    onClose,
    onSaveAbout
}) => {
    const [inputValue, setInputValue] = useState(about || '');
    const [spellCheckedResponse, setSpellCheckedResponse] = useState('');
    const [enhancedResponse, setEnhancedResponse] = useState('');
    const [removeContactInfoModal, setRemoveContactInfoModal] = useState(false);
    const [isChooseBioOpen, setIsChooseBioOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [aboutError, setAboutError] = useState<string>();
    const [rewritesLeft, setRewritesLeft] = useState<number>(5);
    const [rewritesLeftError, setRewritesLeftError] = useState<string>("");

    const getRewritesLeft = async () => {
        try {
            const response = await Api.getAiAssistsLeft();    
            if (response.aiAssistsLeft && typeof(response.aiAssistsLeft === 'number')) {
                setRewritesLeft(response.aiAssistsLeft);
                return response.aiAssistsLeft;
            } else {
                console.error('Invalid response format:', response);
                setRewritesLeftError(tryLaterText);
                setRewritesLeft(0);
                return 0;
            }
        } catch (error) {
            console.error('Error fetching rewrites left:', error);
            setRewritesLeftError(tryLaterText);
            setRewritesLeft(0);
            return 0;
        }
    };

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        getRewritesLeft();
    
        if (rewritesLeft <= 0) {
            setRewritesLeft(0);
            setRewritesLeftError(tryLaterText);
            console.error(rewritesLeftError);
            return;
        }
    
        if (countWords(inputValue) < 3) {
            setAboutError('Tell us how you shine!');
            return;
        }
    
        setAboutError(undefined);
        setLoading(true);
    
        try {
            const message = await Api.checkTextContactInfo(inputValue);
            const messageError = (message.enhanced as any)?.error || (message.spellChecked as any)?.error ? (message.enhanced as any)?.message || (message.spellChecked as any)?.message : undefined;
            if(messageError){
                setAboutError(messageError);
                return;
            }
           
                setSpellCheckedResponse(message.spellChecked);
                setEnhancedResponse(message.enhanced);
                setIsChooseBioOpen(true);
                setRewritesLeft(rewritesLeft - 1);
        } catch (e) {
            console.error('There was an error!', e);
            let errorMessage = "An unexpected error occurred.";
    
            if (e instanceof Error) {
                errorMessage = e.message;
            } else if (typeof e === 'object' && e !== null) {
                const relevantKeys = ['enhanced', 'final', 'spellChecked', 'error', 'message'];
                const relevantEntry = Object.entries(e).find(([key]) => relevantKeys.includes(key));
                if (relevantEntry) {
                    const [key, value] = relevantEntry;
                    errorMessage = `${key}: ${value}`;
                }
            }
    
            setAboutError(errorMessage);
            console.error("Error details:", errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setInputValue(about || '');
    }, [about]);

    useEffect(() => {
        getRewritesLeft();
    }, []);

    const handleInputChange = (event: any) => {
        setInputValue(event.target.value);
    }

    return (
        <>
            {isChooseBioOpen ? <ChooseBio
                isOpen={isChooseBioOpen}
                onBack={() => setIsChooseBioOpen(false)}
                spellCheckedResponse={spellCheckedResponse}
                enhancedResponse={enhancedResponse}
                onSave={(about: string) => {
                    onSaveAbout(about);
                    onClose();
                }}
                removeContactInfoModal={removeContactInfoModal}
                setRemoveContactInfoModal={setRemoveContactInfoModal}
                rewritesLeft={rewritesLeft}
            /> : null}
            <FullscreenModal
                title={'About You'}
                isOpen={isOpen && !isChooseBioOpen}
                buttonText={'Improve Bio'}
                underlinedButtonText={'Skip for Now'}
                onClose={onClose}
                onSubmit={onSubmit}
                stickyBottom={false}
                disabledSubmit={countWords(inputValue) < 3}
                isSignUpPage={true}
            >
                <SimpleModal isOpen={removeContactInfoModal}>
                    <RemoveContactInfoModal onCloseButtonClick={() => setRemoveContactInfoModal(false)}/>
                </SimpleModal>

                <div className={styles.contentContainer}>
                    <header className={clsx('inset', styles.header)}>
                        <p>Hey {name}!</p>
                        <h1>Tell offices how you shine…</h1>
                    </header>
                    <div className="inset">
                        <MaterialInput
                            className={'styledInput'}
                            label={'About You'}
                            defaultValue={"What are your passions in dentistry? Personal interests?..."}
                            id={'about'}
                            name={"about"}
                            type={"text"}
                            value={inputValue}
                            onChange={handleInputChange}
                            multiline={true}
                            error={!!aboutError} 
                            helperText={aboutError}
                            rows={7}
                        />
                    </div>
                    <div className={rewritesLeftError ? styles.screenError : styles.noScreenError}>
                        {rewritesLeftError}
                    </div>
                    <div className={styles.footerNote}>
                        <div>
                            <b>Note: don't include contact information (email, phone, etc.) in this section, it gets removed automatically.</b>
                            <div className={styles.helpText}>
                                <h2>Need help writing?</h2>
                                Don't worry, we've got you. When you hit "Submit" we'll
                                magically fix any spelling & punctuation, and you'll even see an optional
                                version with some flair and personality.
                            </div>
                        </div>
                        <InfoOutlinedIcon className={styles.infoIcon} onClick={() => setRemoveContactInfoModal(true)}/>
                    </div>
                </div>
            </FullscreenModal>
            <FixedLoader
                loading={loading}
            />
        </>
    );
}